import type { Asset, Branch, Company, DepreciationRule, InventoryCheck, LabelTemplate, ServiceCatalogItem } from "../types/imobi";

const now = new Date().toISOString();

export const seedCompany: Company = {
  id: "empresa-demo",
  legalName: "Empresa Demonstração LTDA",
  tradeName: "Empresa Demo",
  cnpj: "00.000.000/0001-00",
  address: "Avenida Central, 1000",
  city: "São Paulo",
  state: "SP",
  zipCode: "01000-000",
  phone: "(11) 3000-0000",
  email: "contato@empresa-demo.com.br",
  status: "ativa",
  internalNotes: "Empresa de demonstração para desenvolvimento inicial.",
  createdAt: now,
  createdBy: "system"
};

export const seedCompanies: Company[] = [
  seedCompany,
  {
    id: "empresa-logistica",
    legalName: "Logística Exemplo S.A.",
    tradeName: "Logística Exemplo",
    cnpj: "11.111.111/0001-11",
    address: "Rua das Operações, 450",
    city: "Curitiba",
    state: "PR",
    zipCode: "80000-000",
    phone: "(41) 3000-1111",
    email: "patrimonio@logistica-exemplo.com.br",
    status: "ativa",
    createdAt: now,
    createdBy: "system"
  },
  {
    id: "empresa-varejo",
    legalName: "Varejo Modelo LTDA",
    tradeName: "Varejo Modelo",
    cnpj: "22.222.222/0001-22",
    address: "Avenida Comercial, 78",
    city: "Belo Horizonte",
    state: "MG",
    zipCode: "30000-000",
    phone: "(31) 3000-2222",
    email: "admin@varejo-modelo.com.br",
    status: "inativa",
    createdAt: now,
    createdBy: "system"
  }
];

export const seedBranches: Branch[] = [
  {
    id: "matriz",
    companyId: seedCompany.id,
    name: "Matriz",
    internalCode: "MTZ",
    address: "Avenida Central, 1000",
    city: "São Paulo",
    state: "SP",
    localManager: "Responsável Local",
    phone: "(11) 3000-0000",
    status: "ativa",
    createdAt: now,
    createdBy: "system"
  }
];

export const seedLocations = ["Administrativo", "Sala Comercial", "Estoque", "Recepção", "Oficina"];

export const seedBrands = ["Dell", "HP", "Lenovo", "Samsung", "NIIMBOT", "Sem marca"];

export const seedDepartments = ["Financeiro", "Vendas", "Operações", "TI", "Diretoria"];

export const seedResponsibles = ["Ana Souza", "Carlos Lima", "Marina Costa", "João Pereira", "Patrimônio Central"];

export const seedDepreciationRules: DepreciationRule[] = [
  { id: "edificacoes", assetTypeName: "Edificações / construções", usefulLifeYears: 25, annualRate: 4, residualValuePercent: 0, method: "linear_mensal", legalReference: "Tabela inicial editável", notes: "Não confundir com terreno.", active: true, createdAt: now, createdBy: "system" },
  { id: "maquinas", assetTypeName: "Máquinas e equipamentos", usefulLifeYears: 10, annualRate: 10, residualValuePercent: 0, method: "linear_mensal", legalReference: "Tabela inicial editável", notes: "Uso geral.", active: true, createdAt: now, createdBy: "system" },
  { id: "moveis", assetTypeName: "Móveis e utensílios", usefulLifeYears: 10, annualRate: 10, residualValuePercent: 0, method: "linear_mensal", legalReference: "Tabela inicial editável", notes: "Mesas, cadeiras e armários.", active: true, createdAt: now, createdBy: "system" },
  { id: "computadores", assetTypeName: "Computadores e periféricos", usefulLifeYears: 5, annualRate: 20, residualValuePercent: 0, method: "linear_mensal", legalReference: "Tabela inicial editável", notes: "Hardware e periféricos.", active: true, createdAt: now, createdBy: "system" },
  { id: "veiculos", assetTypeName: "Veículos leves", usefulLifeYears: 5, annualRate: 20, residualValuePercent: 0, method: "linear_mensal", legalReference: "Tabela inicial editável", notes: "Carros e utilitários.", active: true, createdAt: now, createdBy: "system" },
  { id: "terrenos", assetTypeName: "Terrenos", usefulLifeYears: null, annualRate: 0, residualValuePercent: 0, method: "linear_mensal", legalReference: "Tabela inicial editável", notes: "Normalmente não depreciável.", active: true, createdAt: now, createdBy: "system" }
];

export const seedAssets: Asset[] = [
  {
    id: "asset-001",
    companyId: seedCompany.id,
    branchId: "matriz",
    patrimonyNumber: "PAT-0001",
    internalCode: "IMB-2026-0001",
    description: "Notebook corporativo Dell Latitude com carregador e etiqueta patrimonial.",
    shortDescription: "Notebook Dell Latitude",
    assetTypeId: "computadores",
    physicalLocation: "Administrativo",
    department: "Financeiro",
    responsible: "Ana Souza",
    conservationState: "bom",
    status: "ativo",
    serialNumber: "SN-DEMO-001",
    brand: "Dell",
    model: "Latitude",
    acquisitionValue: 5200,
    currentEstimatedValue: 3900,
    purchaseDate: "2025-03-10",
    inUseDate: "2025-03-15",
    supplier: "Fornecedor Demo",
    estimatedUsefulLifeYears: 5,
    annualDepreciationRate: 20,
    depreciationMethod: "linear_mensal",
    mainPhotoUrl: "https://images.unsplash.com/photo-1496181133206-80ce9b88a853?auto=format&fit=crop&w=800&q=80",
    photosCount: 1,
    auditStatus: "pendente_revisao",
    auditValuation: {
      method: "comparacao_ecommerce",
      value: 3900,
      referenceTitle: "Comparação com notebook equivalente em e-commerce",
      referenceUrl: "https://exemplo.com/produto-equivalente",
      referenceAttachmentUrls: ["storage://companies/empresa-demo/assets/asset-001/audit/reference-ecommerce.pdf"],
      referenceCapturedAt: now,
      notes: "Valor usado como referência externa para precificação auditada."
    },
    createdAt: now,
    createdBy: "system"
  },
  {
    id: "asset-002",
    companyId: seedCompany.id,
    branchId: "matriz",
    patrimonyNumber: "PAT-0002",
    internalCode: "IMB-2026-0002",
    description: "Cadeira ergonômica operacional com regulagem de altura.",
    shortDescription: "Cadeira ergonômica",
    assetTypeId: "moveis",
    physicalLocation: "Sala Comercial",
    department: "Vendas",
    responsible: "Carlos Lima",
    conservationState: "otimo",
    status: "ativo",
    acquisitionValue: 890,
    purchaseDate: "2024-08-02",
    inUseDate: "2024-08-05",
    estimatedUsefulLifeYears: 10,
    annualDepreciationRate: 10,
    depreciationMethod: "linear_mensal",
    photosCount: 0,
    auditStatus: "nao_auditado",
    createdAt: now,
    createdBy: "system"
  }
];

export const seedInventoryChecks: InventoryCheck[] = [
  {
    id: "inventario-001",
    companyId: seedCompany.id,
    branchId: "matriz",
    name: "Balanço Patrimonial Inicial",
    description: "Conferência de demonstração para validar fluxo por QR Code.",
    startedAt: now,
    responsibleUserId: "system",
    participantIds: ["system"],
    includedLocations: ["Administrativo", "Sala Comercial"],
    status: "em_andamento",
    totals: {
      expected: 2,
      verified: 1,
      pending: 1,
      divergent: 0,
      notFound: 0
    },
    createdAt: now,
    createdBy: "system"
  }
];

export const seedLabelTemplates: LabelTemplate[] = [
  {
    id: "template-padrao-50x30",
    name: "Etiqueta padrão 50x30",
    widthMm: 50,
    heightMm: 30,
    marginMm: 2,
    showCompanyLogo: true,
    showImobiLogo: true,
    showQrCode: true,
    showBarcode: true,
    visibleFields: ["patrimonyNumber", "shortDescription", "companyName", "physicalLocation", "responsible", "issuedAt"],
    fontSize: 8,
    printDensity: "normal",
    active: true,
    createdAt: now,
    createdBy: "system"
  },
  {
    id: "template-termica-30x15",
    name: "Etiqueta térmica compacta 30x15",
    widthMm: 30,
    heightMm: 15,
    marginMm: 1,
    showCompanyLogo: false,
    showImobiLogo: true,
    showQrCode: true,
    showBarcode: false,
    visibleFields: ["patrimonyNumber", "shortDescription", "issuedAt"],
    fontSize: 6,
    printDensity: "alta",
    active: true,
    createdAt: now,
    createdBy: "system"
  }
];

export const seedServiceCatalog: ServiceCatalogItem[] = [
  {
    id: "acesso-basico",
    name: "Acesso Basico",
    description: "Licenca mensal para empresas pequenas, ate 300 bens e 2 usuarios.",
    billingType: "mensal",
    baseValue: 199,
    minimumValue: 199,
    minimumQuantity: 1,
    maxAssets: 300,
    maxUsers: 2,
    allowDirectCheckout: true,
    hidePrice: false,
    serviceCategory: "licenca_mensal",
    suggestedRange: "R$ 199/mes",
    valuePerAsset: 0,
    maxDiscountPercent: 20,
    requiresMasterApproval: false,
    releasesAutomatically: true,
    status: "ativo",
    createdAt: now,
    createdBy: "system"
  },
  {
    id: "acesso-gestao",
    name: "Acesso Gestao",
    description: "Licenca mensal para pequenas e medias empresas, ate 1.500 bens e 5 usuarios.",
    billingType: "mensal",
    baseValue: 349,
    minimumValue: 349,
    minimumQuantity: 1,
    maxAssets: 1500,
    maxUsers: 5,
    allowDirectCheckout: true,
    hidePrice: false,
    serviceCategory: "licenca_mensal",
    suggestedRange: "R$ 349/mes",
    valuePerAsset: 0,
    maxDiscountPercent: 20,
    requiresMasterApproval: false,
    releasesAutomatically: true,
    status: "ativo",
    createdAt: now,
    createdBy: "system"
  },
  {
    id: "acesso-pro",
    name: "Acesso Pro",
    description: "Licenca mensal para empresas com varias areas, ate 5.000 bens e 10 usuarios.",
    billingType: "mensal",
    baseValue: 599,
    minimumValue: 599,
    minimumQuantity: 1,
    maxAssets: 5000,
    maxUsers: 10,
    allowDirectCheckout: true,
    hidePrice: false,
    serviceCategory: "licenca_mensal",
    suggestedRange: "R$ 599/mes",
    valuePerAsset: 0,
    maxDiscountPercent: 20,
    requiresMasterApproval: false,
    releasesAutomatically: true,
    status: "ativo",
    createdAt: now,
    createdBy: "system"
  },
  {
    id: "acesso-corporate",
    name: "Acesso Corporate",
    description: "Licenca mensal para bases maiores, acima de 5.000 bens.",
    billingType: "mensal",
    baseValue: 899,
    minimumValue: 899,
    minimumQuantity: 1,
    maxAssets: 0,
    maxUsers: 0,
    allowDirectCheckout: false,
    hidePrice: true,
    serviceCategory: "licenca_mensal",
    suggestedRange: "Sob proposta",
    valuePerAsset: 0,
    maxDiscountPercent: 20,
    requiresMasterApproval: true,
    releasesAutomatically: false,
    status: "ativo",
    createdAt: now,
    createdBy: "system"
  },
  {
    id: "auditoria-patrimonial",
    name: "Auditoria patrimonial",
    description: "Auditoria de bens imobilizados com validação, divergências e relatório.",
    billingType: "por_bem",
    baseValue: 0,
    minimumValue: 500,
    minimumQuantity: 50,
    valuePerAsset: 6,
    serviceCategory: "auditoria",
    suggestedRange: "R$ 3.000 a R$ 20.000+ por projeto",
    maxDiscountPercent: 20,
    requiresMasterApproval: true,
    releasesAutomatically: false,
    status: "ativo",
    createdAt: now,
    createdBy: "system"
  },
  {
    id: "etiquetas-qr-code",
    name: "Emissão de etiquetas QR Code",
    description: "Geração de etiquetas patrimoniais em lote para impressão em PDF ou PNG.",
    billingType: "por_bem",
    baseValue: 0,
    minimumValue: 150,
    minimumQuantity: 1000,
    valuePerAsset: 1.5,
    serviceCategory: "etiquetas",
    suggestedRange: "R$ 1,00 a R$ 4,00 por etiqueta",
    requiresMasterApproval: true,
    releasesAutomatically: false,
    status: "ativo",
    createdAt: now,
    createdBy: "system"
  },
  {
    id: "levantamento-fisico",
    name: "Levantamento fisico",
    description: "Levantamento presencial de bens imobilizados, por diaria, por bem ou por projeto.",
    billingType: "por_bem",
    baseValue: 0,
    minimumValue: 500,
    minimumQuantity: 100,
    valuePerAsset: 4,
    serviceCategory: "levantamento",
    suggestedRange: "R$ 2,00 a R$ 8,00 por bem, ou diaria",
    maxDiscountPercent: 20,
    requiresMasterApproval: true,
    releasesAutomatically: false,
    status: "ativo",
    createdAt: now,
    createdBy: "system"
  },
  {
    id: "balanco-imobilizados",
    name: "Balanco de imobilizados",
    description: "Projeto fechado para conferencia e relatorio de balanco patrimonial de imobilizados.",
    billingType: "personalizado",
    baseValue: 2500,
    minimumValue: 2500,
    minimumQuantity: 1,
    valuePerAsset: 0,
    serviceCategory: "balanco",
    suggestedRange: "R$ 2.500 a R$ 15.000+ por projeto",
    maxDiscountPercent: 20,
    requiresMasterApproval: true,
    releasesAutomatically: false,
    status: "ativo",
    createdAt: now,
    createdBy: "system"
  },
  {
    id: "importacao-planilha",
    name: "Importacao de planilha",
    description: "Importacao e saneamento inicial de planilha patrimonial.",
    billingType: "unico",
    baseValue: 300,
    minimumValue: 300,
    minimumQuantity: 1,
    valuePerAsset: 0,
    serviceCategory: "importacao",
    suggestedRange: "R$ 300 a R$ 1.000",
    maxDiscountPercent: 20,
    requiresMasterApproval: true,
    releasesAutomatically: false,
    status: "ativo",
    createdAt: now,
    createdBy: "system"
  },
  {
    id: "treinamento-remoto",
    name: "Treinamento remoto",
    description: "Treinamento extra de usuarios por hora.",
    billingType: "personalizado",
    baseValue: 250,
    minimumValue: 250,
    minimumQuantity: 1,
    valuePerAsset: 0,
    serviceCategory: "treinamento",
    suggestedRange: "R$ 250 a R$ 500/hora",
    maxDiscountPercent: 20,
    requiresMasterApproval: true,
    releasesAutomatically: false,
    status: "ativo",
    createdAt: now,
    createdBy: "system"
  },
  {
    id: "implantacao-assistida",
    name: "Implantacao assistida",
    description: "Configuracao inicial assistida e implantacao simples da plataforma.",
    billingType: "unico",
    baseValue: 500,
    minimumValue: 500,
    minimumQuantity: 1,
    valuePerAsset: 0,
    serviceCategory: "implantacao",
    suggestedRange: "R$ 500 a R$ 1.500",
    maxDiscountPercent: 20,
    requiresMasterApproval: true,
    releasesAutomatically: false,
    status: "ativo",
    createdAt: now,
    createdBy: "system"
  }
];
